import React, { useState, useMemo } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from '@mui/icons-material';

const SidebarNavItem = ({
  icon: Icon,
  label,
  path,
  children,
  permission = true,
  drawerToggle,
  onNavigate,
  collapsed = false,
  iconStyle = {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isParent = children && children.length > 0;

  const isPathActive = (itemPath, currentPath) => {
    if (!itemPath) return false;
    if (itemPath === '/') return currentPath === '/';
    return currentPath === itemPath ||
      (currentPath.startsWith(itemPath) && currentPath[itemPath.length] === '/');
  };

  const filteredChildren = useMemo(() => {
    const filtered = children?.filter((child) => child.permission !== false) || [];
    const seen = new Set();
    return filtered.filter((child) => {
      if (seen.has(child.path)) return false;
      seen.add(child.path);
      return true;
    });
  }, [children]);

  const childPaths = useMemo(() => filteredChildren.map((c) => c.path), [filteredChildren]);

  const isActive = useMemo(() => {
    if (path) return isPathActive(path, location.pathname);
    return childPaths.some((cp) => isPathActive(cp, location.pathname));
  }, [location.pathname, path, childPaths]);

  const hasActiveChild = useMemo(() => {
    return childPaths.some((cp) => isPathActive(cp, location.pathname));
  }, [location.pathname, childPaths]);

  const shouldAutoExpand = hasActiveChild;

  React.useEffect(() => {
    if (collapsed) {
      setIsOpen(false);
    } else if (shouldAutoExpand && !isOpen) {
      setIsOpen(true);
    }
  }, [collapsed, shouldAutoExpand]);

  const handleClick = () => {
    if (!permission) return;
    if (isParent) {
      if (!collapsed) setIsOpen((o) => !o);
    } else if (path) {
      navigate(path);
      if (drawerToggle) drawerToggle();
      if (onNavigate) onNavigate();
    }
  };

  const handleChildClick = (child) => {
    if (child.onNavigate) child.onNavigate();
    if (child.path && child.path !== '#') {
      navigate(child.path);
    }
    if (drawerToggle) drawerToggle();
    if (onNavigate) onNavigate();
  };

  const itemIsActive = isActive && !isParent;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          height: '2.125rem',
          px: collapsed ? '0.5rem' : '0.75rem',
          mx: '0.5rem',
          mb: '2px',
          borderRadius: '6px',
          cursor: permission ? 'pointer' : 'default',
          position: 'relative',
          opacity: 1,
          backgroundColor: itemIsActive ? 'rgba(255,255,255,0.15)' : 'transparent',
          transition: 'background 0.15s ease',
          '&:hover': {
            backgroundColor: permission
              ? (itemIsActive
                ? 'rgba(255,255,255,0.18)'
                : 'rgba(255,255,255,0.08)')
              : 'transparent',
          },
          // left accent bar for active single items
          '&::before': itemIsActive ? {
            content: '""',
            position: 'absolute',
            left: '-0.5rem',
            top: '25%',
            bottom: '25%',
            width: '3px',
            borderRadius: '0 3px 3px 0',
            backgroundColor: 'rgba(255,255,255,0.9)',
          } : {},
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : '0.625rem',
          flex: collapsed ? 'none' : 1,
          minWidth: 0,
        }}>
          {Icon && (
            <Icon
              size={18}
              color={'white'}
              style={{ flexShrink: 0, strokeWidth: 1.5, ...iconStyle }}
            />
          )}
          {!collapsed && (
            <Typography sx={{
              fontSize: '0.8125rem',
              fontWeight: itemIsActive ? 600 : hasActiveChild ? 500 : 400,
              color: 'white',
              lineHeight: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '0.01em',
            }}>
              {label}
            </Typography>
          )}
        </Box>

        {isParent && !collapsed && (
          <ChevronRight
            sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.35)' }}
            style={{
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </Box>

      {/* Sub-items */}
      {isParent && !collapsed && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ position: 'relative', mx: '0.5rem', mb: '4px' }}>
            {/* vertical guide line aligned with icon center */}
            <Box sx={{
              position: 'absolute',
              left: '1.3rem',
              top: '0.3rem',
              bottom: '0.3rem',
              width: '1px',
              backgroundColor: 'rgba(255,255,255,0.12)',
            }} />

            {filteredChildren.map((child) => {
              const childActive = isPathActive(child.path, location.pathname);
              return (
                <Box
                  key={child.path}
                  onClick={() => handleChildClick(child)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '1.875rem',
                    pl: '2.375rem',
                    pr: '0.625rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: childActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                    transition: 'background 0.15s ease',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: childActive
                        ? 'rgba(255,255,255,0.15)'
                        : 'rgba(255,255,255,0.06)',
                    },
                    // dot indicator for active child
                    '&::before': childActive ? {
                      content: '""',
                      position: 'absolute',
                      left: '1.17rem',
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      backgroundColor: 'white',
                    } : {},
                  }}
                >
                  <Typography sx={{
                    fontSize: '0.8125rem',
                    fontWeight: childActive ? 500 : 400,
                    color: childActive ? 'white' : 'rgba(255,255,255,0.72)',
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.01em',
                  }}>
                    {child.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default SidebarNavItem;
